import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, SearchX } from "lucide-react";
import { toast } from "sonner";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";
import PageHeader from "@/components/dashboard/PageHeader";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";

function timeAgo(iso) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1)  return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}

/**
 * Admin view of unmatched FAQ searches. Every time a user types a query on
 * /faq that returns zero matches, the frontend upserts it into `faq_gaps`.
 * This page shows the top signal so admins can extend the FAQ / KB.
 */
export default function AdminFaqGaps() {
    const [items, setItems]     = useState([]);
    const [loading, setLoading] = useState(false);
    const { sorted: sortedItems, sortKey, direction, requestSort } = useSortableData(items, {
        accessors: {
            query: (i) => i.display || i.query || "",
            hits: (i) => Number(i.count || 0),
            first_seen: (i) => i.first_ts || "",
            last_seen: (i) => i.last_ts || "",
        },
    });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/faq/gaps?limit=200");
            setItems(data.items || []);
        } catch {
            toast.error("Failed to load FAQ gaps");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const totalHits = items.reduce((n, i) => n + (i.count || 0), 0);

    const widgets = [
        {
            id: "faq-gaps-table", title: "Unmatched searches", category: "Content", layout: { x: 0, y: 0, w: 12, h: 10 }, minW: 6, minH: 4,
            render: () => (
                loading && items.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-12 text-center border border-dashed border-border rounded-lg"
                         data-testid="admin-faq-gaps-empty">
                        <SearchX className="w-6 h-6 mx-auto mb-2 opacity-50" />
                        No unmatched searches yet — your FAQ is answering everything so far.
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-xl border border-border bg-card"
                         data-testid="admin-faq-gaps-table">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
                                <tr>
                                    <SortableTh label="Query" sortKey="query" currentKey={sortKey} direction={direction} onSort={requestSort} className="px-4 py-3 font-semibold" />
                                    <SortableTh label="Hits" sortKey="hits" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="px-4 py-3 font-semibold w-24" />
                                    <SortableTh label="First seen" sortKey="first_seen" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="px-4 py-3 font-semibold w-40" />
                                    <SortableTh label="Last seen" sortKey="last_seen" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="px-4 py-3 font-semibold w-40" />
                                </tr>
                            </thead>
                            <tbody>
                                {sortedItems.map((row) => (
                                    <tr key={row.query}
                                        className="border-t border-border hover:bg-muted/30 transition-colors"
                                        data-testid={`admin-faq-gaps-row-${row.query.replace(/\s+/g, "-").slice(0, 24)}`}>
                                        <td className="px-4 py-3 font-medium text-foreground">"{row.display || row.query}"</td>
                                        <td className="px-4 py-3 text-right proportional-nums font-extrabold text-primary">
                                            {row.count}
                                        </td>
                                        <td className="px-4 py-3 text-right text-xs text-muted-foreground whitespace-nowrap">
                                            {timeAgo(row.first_ts)}
                                        </td>
                                        <td className="px-4 py-3 text-right text-xs text-muted-foreground whitespace-nowrap">
                                            {timeAgo(row.last_ts)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ),
        },
    ];

    return (
        <div className="space-y-6" data-testid="admin-faq-gaps-page">
            <PageHeader
                icon={SearchX}
                eyebrow="Admin Tools"
                title="FAQ Gaps"
                subtitle={
                    <>
                        Queries typed into the <code className="text-xs">/faq</code> search that returned zero matches.
                        Use this list to prioritise new FAQ entries or KB additions for Kimani. {items.length} unique
                        quer{items.length === 1 ? "y" : "ies"} · {totalHits} total hits.
                    </>
                }
                actions={
                    <Button variant="outline" size="sm" onClick={load}
                            data-testid="admin-faq-gaps-refresh">
                        <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
                        Reload
                    </Button>
                }
                testId="admin-faq-gaps-header"
            />

            <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-faq-gaps" widgets={widgets} />
        </div>
    );
}
